import useOnScreen from "../../../../hooks/useOnScreen";
import { useRef, useState, type ReactNode, type RefObject } from "react";
import { Badge } from "react-bootstrap";
import { FaChevronDown } from "react-icons/fa";
import { FaTrash } from "react-icons/fa6";
import type { Instance } from "react-table-6";
import { Wrap } from "../../../../../styled-system/jsx";
import { Button } from "../../../../components/core/basic/Button";
import type { CustomFilter } from "../../../../hooks/useTableSettings";
import FilterForm, { getFormatFilterOptions } from "./FilterForm";
import type { SelectorColumn } from "../../../../hooks/useColumnSelector";
import { FilterColumnSelector } from "./FilterColumnSelector";
import { DialogTrigger } from "react-aria-components";
import { formatCurrency, formatNumber, formatPercent } from "../../../../utils/formatNumber";
import { useAppSelector } from "../../../../redux/hooks";
import type { RowData } from "@tanstack/react-table";
import { resolveLabel } from "../../../../components/table/utils";
import { z } from "zod";
import moment from "moment";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { MetricLabel } from "../../utils/metrics";
import type { TSelector } from "../../../../i18n/types";
import { type AppTable } from "../../../../components/table/tableFeatures";

type FilterSelectOption = {
  label: string | TSelector;
  value: unknown;
  clearable?: boolean;
  description?: string | TSelector;
};

const formatToInitialValueMap = {
  currency: "0",
  number: "0",
  percent: "0",
  text: "",
  date: [moment().subtract(1, "month").toISOString(), moment().toISOString()],
  select: ["all"],
  "single-select": "all",
  "multi-select": ["all"],
  "media-plan": ["all"],
  toggle: "on",
};

const formatFilterValue = ({
  value,
  format,
  selectOptions = [],
  marketPlace,
  t,
}: {
  value: unknown;
  format?: string;
  selectOptions?: { value: unknown; label: string }[];
  marketPlace: string;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any, any>;
}): ReactNode => {
  // If input can't be parsed into number format, return blank string to avoid NaNs from rendering
  if (["currency", "number", "percent"].includes(format!)) {
    try {
      parseFloat(value as string);
    } catch {
      return "";
    }
  }

  switch (format) {
    case "single-select":
      return selectOptions.find((o) => o.value === value)?.label;
    case "select":
    case "multi-select":
      return Array.isArray(value) ?
          selectOptions.filter((o) => value.includes(o.value)).map((selected) => selected.label)
        : selectOptions.find((option) => option.value === value)?.label;
    case "currency":
      return formatCurrency(value as number, {
        marketPlace: marketPlace,
        compact: true,
      });
    case "number":
      return formatNumber(value as number);
    case "percent":
      return formatPercent(value as number);
    case "date":
      return moment(value as string).format("MM-DD-YYYY");
    case "media-plan":
      if (Array.isArray(value)) {
        if (value.includes("all")) return t(($) => $.common["All"]);
        return t(($) => $.merchandising["{count} selected"], { count: value.length });
      }
      return value as ReactNode;
    case "text":
    default:
      return value as ReactNode;
  }
};

const getOperatorDisplay = (op: string) => {
  if (op === "anyof") {
    return "⊃";
  }
  if (op === "allof") {
    return "=";
  }

  return op;
};

export interface ColumnOption {
  name?: MetricLabel | string;
  format?: string;
  id?: string;
  onValue?: string;
  offValue?: string;
  filterType?: string;
  override?: boolean;
  defaultValue?: unknown;
  operator?: string;
  selectOptions?: FilterSelectOption[];
  filterFunction?: (row: unknown, value: unknown) => boolean;
}

interface Props<T> {
  tableRef?: RefObject<Instance<T> | null>;
  customFilters: CustomFilter[];
  setCustomFilters?: (updatedFilters: CustomFilter[]) => void;
  additionalFilterOptions?: ColumnOption[];
  excludedFilterColumns?: string[];
  initialFilters?: CustomFilter[];
  tableId: string;
}

export const RT6FilterWidget = <T,>({
  tableRef,
  customFilters,
  setCustomFilters,
  additionalFilterOptions = [],
  excludedFilterColumns = [],
}: Props<T>) => {
  const { t } = useTranslation(["merchandising", "common", "metrics", "advertising"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const [showForm, setShowForm] = useState<string | null>(null);
  const widgetVisiblityRef = useRef<HTMLDivElement>(null);

  const columnOptions =
    (
      tableRef?.current?.props.columns as (SelectorColumn & { name?: MetricLabel; Header?: unknown })[] | undefined
    )?.map((col): ColumnOption => {
      const { name, format, id, onValue, offValue, Header } = col;
      // Mirror useColumnSelector: legacy columns built with `makeColumn` carry a
      // translated string `Header` but no `name` selector; prefer it over falling
      // through to the raw column id (which surfaces as e.g. `Ad_sales`/`Roas` in
      // the filter pill and "Add filter" dropdown).
      const resolvedName = name ?? (typeof Header === "string" ? Header : undefined);
      return { name: resolvedName, format, id, onValue, offValue };
    }) ?? [];

  const allSources: ColumnOption[] = [
    ...additionalFilterOptions,
    ...columnOptions.filter((c) => ![...excludedFilterColumns, "isExpanded"].includes(c.id!)),
  ];

  const filterables = [
    ...additionalFilterOptions,
    ...columnOptions
      .filter((c) => ![...excludedFilterColumns, "isExpanded"].includes(c.id!))
      .filter(
        (option) => option.format && !customFilters.map((selectedFilter) => selectedFilter.id).includes(option.id!),
      ),
  ];

  // Below resolves issues where tables hidden within closed accordion doesn't reliably create a new table ref
  // in time to load column filter options
  useOnScreen(widgetVisiblityRef);

  return (
    <Wrap ref={widgetVisiblityRef} gap="xsmall" fontSize="medium">
      {customFilters.map((filter, index) => {
        const { id, format, operator, value, min, max } = filter;
        const source = allSources.find((s) => s.id === id);
        // Module filter defs arrive pre-resolved (strings) from per-module hooks;
        // column-derived names are still `metrics` selectors that this widget binds.
        const label = typeof source?.name === "function" ? t(source.name) : (source?.name ?? id);
        const selectOptions = (source?.selectOptions ?? []).map((o) => ({
          ...o,
          label: typeof o.label === "function" ? t(o.label) : o.label,
        }));

        const formattedValue = formatFilterValue({ value, format, selectOptions, marketPlace, t });
        return (
          <DialogTrigger key={id} isOpen={showForm === id} onOpenChange={(isOpen) => setShowForm(isOpen ? id : null)}>
            <Button variant={"contentOnly"}>
              <Badge
                className="filter-badge bg-light border border-muted text-muted py-2 px-4 fs-5 p-relative"
                pill
                onClick={() => setShowForm(id)}
                style={{ position: "relative", cursor: "pointer" }}
              >
                {label}
                {operator === "-" ?
                  <span className="ps-1 fs-6">
                    <span>{formatFilterValue({ value: min, format, marketPlace, t }) ?? ""}</span>
                    {` - `}
                    <span>{formatFilterValue({ value: max, format, marketPlace, t }) ?? ""}</span>
                  </span>
                : <>
                    <span className="ps-1 fs-6">{getOperatorDisplay(operator)} </span>
                    <span className="fs-6">
                      {Array.isArray(formattedValue) ?
                        <span>
                          {formattedValue[0] as ReactNode}
                          {formattedValue.length > 1 && <span> +{formattedValue.length - 1}</span>}
                        </span>
                      : (formatFilterValue({ value, format, selectOptions, marketPlace, t }) ?? "")}
                    </span>
                  </>
                }
                <FaChevronDown className="ps-3 fs-2" />
              </Badge>
            </Button>

            <FilterForm
              format={filter.format}
              title={label}
              onApply={(filter) => {
                const updatedFilters = customFilters.map((cf) => {
                  if (cf.id === id) {
                    const nextFilter = { ...cf };
                    nextFilter.operator = filter.op;
                    if (filter.op === "-") {
                      nextFilter.min = filter.value[0];
                      nextFilter.max = filter.value[1];
                    } else {
                      nextFilter.value = filter.value;
                    }
                    return nextFilter;
                  }
                  return cf;
                });
                setCustomFilters?.(updatedFilters);
              }}
              onPressRemove={() => {
                const updatedFilters = customFilters.filter((f, filterIndex) => filterIndex !== index);
                setCustomFilters?.(updatedFilters);
              }}
              initialValues={{
                op: filter.operator,
                value: filter.operator === "-" ? [filter.min, filter.max] : filter.value,
              }}
              selectOptions={selectOptions}
            />
          </DialogTrigger>
        );
      })}

      <FilterColumnSelector
        filterOptions={filterables.map((option) => ({
          id: option.id!,
          label: typeof option.name === "function" ? t(option.name) : (option.name ?? option.id!),
        }))}
        onSelect={(id) => {
          const selectedOption = filterables.find((option) => option.id === id);
          if (!selectedOption) {
            return;
          }
          setCustomFilters?.([
            ...customFilters,
            {
              id: selectedOption.id!,
              format: selectedOption.filterType ?? selectedOption.format,
              operator: selectedOption.override ? "=" : ">",
              value: selectedOption.defaultValue ?? null,
              onValue: selectedOption.onValue,
              offValue: selectedOption.offValue,
              override: selectedOption.override ?? false,
              filterFunction: selectedOption.filterFunction,
            },
          ]);
          setShowForm(id);
        }}
      />

      {customFilters.length ?
        <Button variant={"contentOnly"} className="fs-5 text-muted" onPress={() => setCustomFilters?.([])}>
          <FaTrash /> {t(($) => $.merchandising["Clear filters"], { count: customFilters.length })}
        </Button>
      : null}
    </Wrap>
  );
};

export const RT8FilterWidget = <TData extends RowData>({ table }: { table: AppTable<TData> }) => {
  const { t } = useTranslation(["merchandising", "common", "metrics", "advertising"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const [showForm, setShowForm] = useState<string | null>(null);

  const filterOptions = table
    .getAllColumns()
    .filter((col) => col.getCanFilter() && !col.getIsFiltered())
    .map((col) => ({
      id: col.id,
      label: resolveLabel(col, "columnFilterOptions", (s) => t(s)),
    }));
  const filters = table.state.columnFilters;

  return (
    <Wrap gap="small">
      {filters.map((filter) => {
        const parsedFilter = z
          .object({
            id: z.string(),
            value: z
              .object({
                op: z.string(),
                value: z.unknown(),
              })
              .transform(({ op, value }) => ({ op, value })),
          })

          .parse(filter);

        const column = table.getColumn(filter.id);
        if (!column) {
          return null;
        }
        const label = resolveLabel(column, "columnFilterOptions", (s) => t(s));
        const format = column.columnDef.meta?.columnFilterOptions?.format ?? "text";
        const selectOptions = (table.options.meta?.columnFilterSelectOptions?.[filter.id] ?? []).map((o) => ({
          ...o,
          label: typeof o.label === "function" ? t(o.label) : o.label,
        }));
        const operator = parsedFilter.value.op;
        const value = parsedFilter.value.value;

        const formattedValue = formatFilterValue({ value, format, selectOptions, marketPlace, t });

        return (
          <DialogTrigger
            key={filter.id}
            isOpen={showForm === filter.id}
            onOpenChange={(isOpen) => setShowForm(isOpen ? filter.id : null)}
          >
            <Button variant={"contentOnly"}>
              <Badge
                className="filter-badge bg-light border border-muted text-muted py-2 px-4 fs-5 p-relative"
                pill
                onClick={() => setShowForm(filter.id)}
                style={{ position: "relative", cursor: "pointer" }}
              >
                {label}
                {operator === "-" ?
                  <span className="ps-1 fs-6">
                    <span>
                      {formatFilterValue({
                        value: (parsedFilter.value.value as string[])[0],
                        format,
                        marketPlace,
                        t,
                      }) ?? ""}
                    </span>
                    -
                    <span>
                      {formatFilterValue({
                        value: (parsedFilter.value.value as string[])[1],
                        format,
                        marketPlace,
                        t,
                      }) ?? ""}
                    </span>
                  </span>
                : <>
                    <span className="ps-1 fs-6">{getOperatorDisplay(operator)} </span>
                    <span className="fs-6">
                      {Array.isArray(formattedValue) ?
                        <span>
                          {formattedValue[0] as ReactNode}
                          {formattedValue.length > 1 && <span> +{formattedValue.length - 1}</span>}
                        </span>
                      : (formatFilterValue({ value, format, selectOptions, marketPlace, t }) ?? "")}
                    </span>
                  </>
                }
                <FaChevronDown className="ps-3 fs-2" />
              </Badge>
            </Button>

            <FilterForm
              format={format}
              title={label}
              onApply={(filterValue) => {
                column.setFilterValue(filterValue);
              }}
              onPressRemove={() => {
                column.setFilterValue(undefined);
              }}
              initialValues={parsedFilter.value}
              selectOptions={selectOptions}
            />
          </DialogTrigger>
        );
      })}

      <FilterColumnSelector
        filterOptions={filterOptions}
        onSelect={(id) => {
          const column = table.getColumn(id);
          const format = column?.columnDef.meta?.columnFilterOptions?.format ?? "text";
          const op = getFormatFilterOptions(format)[0]?.value ?? "=";
          const initialValue = formatToInitialValueMap[format];
          column?.setFilterValue({ op, value: initialValue });

          setShowForm(id);
        }}
      />
      {filters.length ?
        <Button variant={"contentOnly"} className="fs-5 text-muted" onPress={() => table.resetColumnFilters(true)}>
          <FaTrash /> {t(($) => $.merchandising["Clear filters"], { count: filters.length })}
        </Button>
      : null}
    </Wrap>
  );
};
